// src/data/jornadaStore.js
import { OnlineAdapter } from "./adapters/onlineAdapter";
import { OfflineAdapter } from "./adapters/offlineAdapter";

// --- utils storage seguros ---
const jget = (k, def = null) => { try { const s = localStorage.getItem(k); return s ? JSON.parse(s) : def; } catch { return def; } };
const jset = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} };
const jdel = (k) => { try { localStorage.removeItem(k); } catch {} };
const uuid = () => (crypto?.randomUUID?.() || `k_${Date.now()}_${Math.random().toString(36).slice(2)}`);

const KEYS = {
  centros: "pwa.cache.centros",
  localActive: "pwa.jornada.activa",
};

function nowIso() { return new Date().toISOString(); }

class JornadaStore {
  constructor() {
    this._online = !!navigator.onLine;
    this._active = null;  // lo que la UI debe ver (server o local)
    this._local  = null;  // activa nacida offline o stub pendiente (id "local:*")
    this._reconciling = false;
    this._listeners = new Set();
  }

  // -------- eventos / snapshot --------
  subscribe(fn) { this._listeners.add(fn); return () => this._listeners.delete(fn); }
  _emit(evt = {}) { for (const fn of this._listeners) { try { fn(evt); } catch {} } }
  snapshot() {
    return {
      online: this._online,
      active: this._active,
      local:  this._local,
      reconciling: this._reconciling,
      centrosCached: jget(KEYS.centros, []),
    };
  }

  // -------- helpers internos --------
  _loadLocalActive() {
    const loc = jget(KEYS.localActive, null);
    this._local  = loc || null;
    // preferimos mostrar la local si no está marcada como pending*
    if (this._local && !(this._local.pendingFinish || this._local.pendingCancel)) {
      this._active = this._local;
    }
  }
  _saveLocalActive(obj) {
    this._local  = obj || null;
    this._active = obj || null;
    if (obj) jset(KEYS.localActive, obj); else jdel(KEYS.localActive);
  }
  _clearLocalActive() {
    this._local = null;
    jdel(KEYS.localActive);
    // si la activa apuntaba a la local, la ocultamos; el servidor decidirá
    if (this._active && typeof this._active.id === "string" && this._active.id.startsWith("local:")) {
      this._active = null;
    }
  }

  // -------- red --------
  setOnline(v) {
    const was = this._online;
    this._online = !!v;
    this._emit({ type: "net:set", online: this._online });
    if (!was && this._online) {
      // al recuperar red → reconcilia
      this.reconcile().catch(() => {});
    }
  }

  // -------- bootstrap --------
  async bootstrap() {
    // 1) centros cache
    if (this._online) {
      try {
        const centros = await OnlineAdapter.getCentros();
        if (Array.isArray(centros) && centros.length) jset(KEYS.centros, centros);
      } catch {}
    }
    // 2) activa preferente
    this._loadLocalActive();
    if (this._online) {
      try {
        const s = await OnlineAdapter.getActive(); // null si 204
        if (s) {
          this._active = s;
          this._clearLocalActive(); // descarta local si la hubiera
        }
      } catch {}
    }
    this._emit({ type: "bootstrap" });

    // 3) al estar online intenta reconciliar la outbox
    if (this._online) {
      await this.reconcile().catch(() => {});
    }
  }

  // -------- lecturas --------
  getActive() { return this._active; }
  getLocal()  { return this._local;  }

  async getCentros() {
    const cached = jget(KEYS.centros, []);
    if (cached?.length) return cached;
    if (this._online) {
      const centros = await OnlineAdapter.getCentros();
      if (Array.isArray(centros) && centros.length) jset(KEYS.centros, centros);
      return centros;
    }
    return [];
  }

  // -------- mutaciones UI --------
  async start({ centroId, centroNombre, latitud, longitud, fingerprint }) {
    // ⚠️ Si hay activa o local (incluida pendiente), no iniciar otra
    if (this._active || this._local) {
      return { status: "noop" };
    }

    const ts = nowIso();
    const common = {
      centro_id: Number(centroId),
      latitud,
      longitud,
      fingerprint,
      inicio: ts,
      inicio_client: ts, // usado en replays offline
    };

    // Intento ONLINE con fallback robusto a OFFLINE
    if (this._online) {
      try {
        const r = await OnlineAdapter.start(common);
        if (r?.ok && !r.conflict) {
          const s = await OnlineAdapter.getActive().catch(() => null);
          if (s) {
            this._active = s;
            this._clearLocalActive();
            this._emit({ type: "start:online", active: this._active });
            return { status: "online", data: s };
          }
          // poco probable: server sin activa → cae a offline
        } else if (r?.status === 409 || r?.conflict) {
          // ya hay activa en servidor → recupérala
          const s = await OnlineAdapter.getActive().catch(() => null);
          if (s) {
            this._active = s;
            this._clearLocalActive();
            this._emit({ type: "start:conflict", active: this._active });
            return { status: "online", data: s };
          }
        }
      } catch {
        // red caida/timeout → fallback a offline
      }
    }

    // OFFLINE inmediato: crea activa local y encola (lo hace el OfflineAdapter)
    const local = OfflineAdapter.start({
      ...common,
      centro_nombre: centroNombre || "",
      // debe crear outbox START con groupKey y payload = common
    });
    // esperamos que OfflineAdapter.start devuelva { active }
    const activeLocal = local?.active || {
      id: `local:${uuid()}`,
      centro_id: Number(centroId),
      centro_nombre: centroNombre || "",
      inicio: common.inicio,
      latitud_inicio: latitud,
      longitud_inicio: longitud,
      pendingFinish: false,
      pendingCancel: false,
    };
    this._saveLocalActive(activeLocal);
    this._emit({ type: "start:offline", active: this._active });
    return { status: "offline", data: { local: activeLocal } };
  }

  async finish({ latitud, longitud, enRango, metros }) {
    if (!this._active && !this._local) return { status: "noop" };

    const current = this._active || this._local;
    const isLocal =
      typeof current.id === "string" && current.id.startsWith("local:");

    const basePayload = {
      motivo_fin: "finalizada",
      latitud_cierre: latitud,
      longitud_cierre: longitud,
      en_rango_cierre: !!enRango,
      distancia_m_cierre: Math.round(metros || 0),
    };

    // 🔁 OFFLINE (jornada nacida offline O nacida online pero estamos sin red)
    if (isLocal || !this._online) {
      const offlinePayload = {
        ...basePayload,
        fin_client: nowIso(),
        // contexto opcional para que OfflineAdapter pueda crear stub si no hay localActive
        centro_id: current.centro_id ?? null,
        centro_nombre: current.centro_nombre || "",
        inicio: current.inicio ?? null,
        latitud_inicio: current.latitud_inicio ?? null,
        longitud_inicio: current.longitud_inicio ?? null,
      };

      const res = OfflineAdapter.finish(offlinePayload) || {};
      if (res.active) {
        this._saveLocalActive(res.active);
      }

      this._emit({ type: "finish:queued", active: this._active });
      return { status: "offline" };
    }

    // 🌐 ONLINE directo
    await OnlineAdapter.finishActual(basePayload);
    this._active = null;
    this._emit({ type: "finish:online" });
    return { status: "online" };
  }

  async cancel() {
    if (!this._active && !this._local) return { status: "noop" };

    const current = this._active || this._local;
    const isLocal =
      typeof current.id === "string" && current.id.startsWith("local:");

    if (isLocal || !this._online) {
      const res = OfflineAdapter.cancel() || {};
      if (res.active) {
        this._saveLocalActive(res.active);
      }
      this._emit({ type: "cancel:queued", active: this._active });
      return { status: "offline" };
    }

    await OnlineAdapter.cancelActual();
    this._active = null;
    this._emit({ type: "cancel:online" });
    return { status: "online" };
  }

  // -------- reconciliación --------
  async reconcile() {
    if (!this._online || this._reconciling) return;
    this._reconciling = true; this._emit({ type: "reconcile:start" });

    try {
      const list = (OfflineAdapter._outboxList?.() || []).sort((a, b) => a.ts - b.ts);
      if (!list.length) {
        // refresco de estado server
        const s = await OnlineAdapter.getActive().catch(() => null);
        this._active = s || null;
        if (s) this._clearLocalActive();
        this._emit({ type: "reconcile:idle", active: this._active });
        return;
      }

      // agrupa por groupKey (una jornada offline completa o stub híbrido)
      const groups = new Map();
      for (const it of list) {
        const key = it.groupKey || "default";
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(it);
      }

      for (const [groupKey, items] of groups) {
        const start  = items.find(x => x.type === "START");
        const finish = items.find(x => x.type === "FINISH");
        const cancel = items.find(x => x.type === "CANCEL");
        const ids    = items.map(x => x.id);

        // C) Solo FINISH/CANCEL (inició online, cerró/canceló offline)
        if (!start && (finish || cancel)) {
          try {
            if (finish) {
              await OnlineAdapter.finishActual(
                finish.payload,
                { offlineReplay: true }
              );
            } else {
              await OnlineAdapter.cancelActual({ offlineReplay: true });
            }
            OfflineAdapter._outboxRemoveIds?.(ids);
            this._clearLocalActive();
          } catch (e) {
            // si falla (red, 4xx/5xx) se reintentará en la siguiente reconcil.
            continue;
          }
          continue;
        }

        // A) Solo START (nació offline)
        if (start && !finish && !cancel) {
          try {
            const r = await OnlineAdapter.start(
              start.payload,
              {
                offlineReplay: true,
                idemKey: start.id || groupKey,
              }
            );
            if (r?.ok || r?.status === 409) {
              // tanto ok como 409 → existe activa en server
              const s = await OnlineAdapter.getActive().catch(() => null);
              if (s) {
                this._active = s;
                this._clearLocalActive();
              }
              OfflineAdapter._outboxRemoveIds?.(ids);
            } else {
              continue;
            }
          } catch {
            continue; // red caída → reintento posterior
          }
          continue;
        }

        // B) START + FINISH/CANCEL (ciclo completo offline)
        if (start && (finish || cancel)) {
          try {
            // 1) intenta crear la jornada con inicio_client
            await (async () => {
              try {
                await OnlineAdapter.start(
                  start.payload,
                  {
                    offlineReplay: true,
                    idemKey: start.id || groupKey,
                  }
                );
              } catch (err) {
                // si da 409 en start, el caller ya lo trata como ok (start() lo convierte)
                if (!(err instanceof Error)) throw err;
                throw err;
              }
            })();

            // 2) cerrar/cancelar con fin_client (si hay FINISH)
            if (finish) {
              await OnlineAdapter.finishActual(
                finish.payload,
                { offlineReplay: true }
              );
            } else {
              await OnlineAdapter.cancelActual({ offlineReplay: true });
            }

            OfflineAdapter._outboxRemoveIds?.(ids);
            this._clearLocalActive();
            this._active = null;
          } catch {
            continue; // reintento posterior
          }
          continue;
        }
      }

      // refresco final desde servidor
      const s = await OnlineAdapter.getActive().catch(() => null);
      this._active = s || null;
      if (s) this._clearLocalActive();
      this._emit({ type: "reconcile:done", active: this._active });
    } finally {
      this._reconciling = false; this._emit({ type: "reconcile:end" });
    }
  }
}

export const jornadaStore = new JornadaStore();
